"use client";

import { useTheme } from "next-themes";
import { useEffect } from "react";
import { toast } from "sonner";

const THEME_CYCLE = ["light", "dark", "system"] as const;

const THEME_LABEL: Record<(typeof THEME_CYCLE)[number], string> = {
  light: "Light mode on",
  dark: "Dark mode on",
  system: "System theme on",
};

/**
 * Easter-egg keyboard shortcut to cycle the theme: Shift + D ("D" for dark)
 * rotates light → dark → system. Ignored while typing in a field and when other
 * modifier keys are held, so it never clobbers native browser shortcuts. Shows a
 * confirmation toast on each change.
 */
export function ThemeEasterEgg() {
  const { theme, setTheme } = useTheme();

  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (e.metaKey || e.ctrlKey || e.altKey || e.repeat || !e.shiftKey) return;
      if (e.key.toLowerCase() !== "d") return;

      const target = e.target as HTMLElement | null;
      if (
        target &&
        (target.isContentEditable ||
          target.tagName === "INPUT" ||
          target.tagName === "TEXTAREA" ||
          target.tagName === "SELECT")
      ) {
        return;
      }

      e.preventDefault();
      const current = THEME_CYCLE.indexOf((theme ?? "system") as (typeof THEME_CYCLE)[number]);
      const next = THEME_CYCLE[(current + 1) % THEME_CYCLE.length];
      setTheme(next);
      toast(THEME_LABEL[next], {
        description: "Press Shift + D to cycle themes",
      });
    }

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [theme, setTheme]);

  return null;
}
